import { httpClient } from '../httpClient';

export const dsrApi = {
  getAll: (params) =>
    httpClient.get('/dsr/requests', { params }),

  getById: (id) =>
    httpClient.get(`/dsr/${id}`),

  create: (data) =>
    httpClient.post('/dsr/requests', data),

  update: (id, data) =>
    httpClient.put(`/dsr/${id}`, data),

  delete: (id) =>
    httpClient.delete(`/dsr/${id}`),

  process: (id, data) =>
    httpClient.post(`/dsr/${id}/process`, data),

  download: (id) =>
    httpClient.get(`/dsr/${id}/download`, { responseType: 'blob' }),

  getStatistics: () =>
    httpClient.get('/dsr/statistics'),

  addActivity: (id, data) =>
    httpClient.post(`/dsr/${id}/activity`, data),

  getDashboard: () =>
    httpClient.get('/dsr/dashboard'),

  // T278/T280 (FR-031) — server-rendered bilingual DSR request-summary PDF.
  // Replaces the Arabic-blind client jsPDF renderer (exportDSRPDF).
  summaryPdf: (id, locale) =>
    httpClient.get(`/dsr/${id}/summary-pdf`, {
      params: { locale },
      responseType: 'blob',
    }),

  // T309 — bilingual PDF access/right-to-know report
  accessReport: (id, locale) =>
    httpClient.get(`/dsr/${id}/access-report`, {
      params: { locale },
      responseType: 'blob',
    }),

  // T310 — portability export (JSON or CSV)
  portabilityExport: (id, format = 'json') =>
    httpClient.get(`/dsr/${id}/portability-export`, {
      params: { format },
      responseType: 'blob',
    }),

  // T502 — list generated notifications + sent status
  listNotifications: (id) =>
    httpClient.get(`/dsr/${id}/notifications`),

  // T501 — mark a notification as sent
  markNotificationSent: (id, notificationType, data) =>
    httpClient.post(`/dsr/${id}/notifications/${notificationType}/mark-sent`, data),

  // T501 — mark refusal letter as sent
  markRefusalLetterSent: (id, data) =>
    httpClient.post(`/dsr/${id}/refusal-letter/mark-sent`, data),

  // T809 — identity verification
  uploadVerificationArtifact: (id, formData) =>
    httpClient.post(`/dsr/${id}/verify-upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  markVerified: (id) =>
    httpClient.post(`/dsr/${id}/verify-mark`),

  // T809 — dry-run preview + confirm process
  dryRunPreview: (id, data) =>
    httpClient.post(`/dsr/${id}/process`, { ...data, dry_run: true }),

  processConfirm: (id, data) =>
    httpClient.post(`/dsr/${id}/process`, data),

  // T809 — deadline extension
  requestExtension: (id, data) =>
    httpClient.post(`/dsr/${id}/request-extension`, data),

  // T809 — refusal flow
  refuseRequest: (id, data) =>
    httpClient.post(`/dsr/${id}/request-refusal`, data),

  decideRefusal: (id, data) =>
    httpClient.post(`/dsr/${id}/decide-refusal`, data),

  generateRefusalLetter: (id, locale = 'en') =>
    httpClient.get(`/dsr/${id}/refusal-letter`, { params: { locale }, responseType: 'blob' }),

  // T1503 / T809 / S6 — third-party notification obligation (cascade).
  // GET now reads the DSR-scoped snapshot (never re-queries ROPA) and always
  // returns 200 — `blocked` is a state in the body, not an HTTP error. See
  // the S6 build sheet §5.3 for the pinned payload shape.
  getCascadeRecipients: (id) =>
    httpClient.get(`/dsr/${id}/cascade-recipients`),

  // S6 T2/T4 — takes the recipient snapshot from ROPA (first resolution) or
  // retries it (after a prior `blocked`). NEW.
  resolveCascadeRecipients: (id) =>
    httpClient.post(`/dsr/${id}/cascade-recipients/resolve`, {}),

  // S6 D4 "add" — DPO adds a recipient the register missed. `reason` required.
  // NEW.
  addCascadeRecipient: (id, data) =>
    httpClient.post(`/dsr/${id}/cascade-recipients`, data),

  // rowId is the DSRNotificationRecipient snapshot row id (NOT the ROPA
  // DisclosureEvent id — S6 changed this path param, see build sheet §8.0).
  generateCascadeBody: (id, rowId, locale) =>
    httpClient.post(`/dsr/${id}/cascade-recipients/${rowId}/generate-body`, { locale }),

  // rowId — see generateCascadeBody comment. `recipient_name` is deliberately
  // NOT sent: the snapshot row already holds the name (S6 build sheet §5.1).
  markCascadeRecipientSent: (id, rowId, data) =>
    httpClient.post(`/dsr/${id}/cascade-recipients/${rowId}/mark-sent`, data),

  // S6 D4 "remove" — DPO removes a snapshotted recipient. `reason` required.
  // NEW.
  removeCascadeRecipient: (id, rowId, data) =>
    httpClient.post(`/dsr/${id}/cascade-recipients/${rowId}/remove`, data),

  // S6 D5 — the DPO's deliberate attestation that the notification duty is
  // discharged. The server derives none/notified from the retained-row
  // count; the DPO does not choose it. NEW.
  attestNotification: (id, data) =>
    httpClient.post(`/dsr/${id}/notification-attestation`, data),

  // S6 T11 — reopens a discharged attestation for re-work (e.g. a legal
  // status transition after closure). Not yet wired to any UI control —
  // kept for a future reopen surface, same "kept for future" precedent as
  // sendOwnerEmail below. NEW.
  reopenNotificationAttestation: (id, data) =>
    httpClient.post(`/dsr/${id}/notification-attestation/reopen`, data),

  // T809 — destruction certificate
  downloadDestructionCert: (id) =>
    httpClient.get(`/dsr/${id}/destruction-certificate`, { responseType: 'blob' }),

  verifyDestructionCertSignature: (id) =>
    httpClient.post(`/dsr/${id}/destruction-certificate/verify`),

  // Phase 3 (Brand Level 1) — live public presences, for the queue filter.
  listPresences: () =>
    httpClient.get('/dsr/presences'),

  // T809 — inbound API key management.
  //
  // `revokeInboundAPIKey` / `rotateInboundAPIKey` were DELETED here on
  // 2026-08-23. Neither ever had a caller — the page's hooks
  // (`modules/dsr/hooks/useDSRInboundAPIKeys.js`) build their own requests —
  // and the revoke one carried the bodiless shape that PR #204 turned into a
  // guaranteed 401 `step_up_required` (routes.py:959). Leaving a dead helper
  // with a broken wire format is an invitation for the next caller to adopt
  // it. Revoke goes through `setup/useDSRSetup.js` → `revokeCredential`,
  // which sends the step-up `password`.
  //
  // The two below are equally callerless today; they are kept because they
  // are a correct wire format for routes that take no step-up.
  listInboundAPIKeys: () =>
    httpClient.get('/dsr/inbound-api-keys'),

  createInboundAPIKey: (data) =>
    httpClient.post('/dsr/inbound-api-keys', data),

  // T809 — sectoral overlays
  listActiveOverlays: () =>
    httpClient.get('/dsr/active-overlays'),

  // B-02 — Command Center aggregate feed (6 widgets)
  getCommandCenter: () =>
    httpClient.get('/dsr/command-center'),

  // Phase 4 §9 — the operational alert feed (coverage, key anomalies, caps)
  listAlerts: (reason) =>
    httpClient.get('/dsr/alerts', reason ? { params: { reason } } : undefined),

  listAlertsForRequest: (id) =>
    httpClient.get(`/dsr/${id}/alerts`),

  acknowledgeAlert: (alertId, reason) =>
    httpClient.post(`/dsr/alerts/${alertId}/acknowledge`, { reason }),

  // B-03 — File SDAIA complaint against a DSR outcome
  fileComplaint: (id, data) =>
    httpClient.post(`/dsr/${id}/complaint`, data),

  // B-04 — Auto-fulfillment compilation preview
  getCompilation: (id) =>
    httpClient.get(`/dsr/${id}/compilation`),

  // H-14 — Hard-copy delivery confirmation (PORTABILITY D.3)
  confirmHardCopyDelivery: (id, notes) =>
    httpClient.post(`/dsr/${id}/confirm-hardcopy-delivery`, { notes }),

  // Spec §5.4 — 3-option search strategy + per-source notification
  searchPreview: (id) =>
    httpClient.get(`/dsr/${id}/search/preview`),

  startSearch: (id, data) =>
    httpClient.post(`/dsr/${id}/search`, data),

  getSearchResults: (id) =>
    httpClient.get(`/dsr/${id}/search/results`),

  notifyOwners: (id, data) =>
    httpClient.post(`/dsr/${id}/notify-owners`, data),

  // Bulk send for every unnotified search hit. Response is deliberately
  // three-valued so the UI can tell the cases apart WITHOUT over-claiming:
  //   { notified, prepared, skipped, emailsSent }
  // `prepared` = bodies handed to the relay, `emailsSent` = bodies the relay
  // ACCEPTED. prepared > emailsSent means some owners were genuinely not
  // sent to. The idempotent "nothing new to do" reply omits prepared and
  // emailsSent entirely — treat a missing `prepared` as zero, not as failure.
  // Routed through httpClient (not raw fetch) so the request carries the
  // X-CSRF-Token header; JWT_COOKIE_CSRF_PROTECT is on, and a cookie-only
  // POST is rejected with 401.
  notifyAllHits: (id, data) =>
    httpClient.post(`/dsr/${id}/notify-all-hits`, data),

  // Spec §6 — preview of the email body BEFORE minting a real token.
  // Backend: GET /api/dsr/<id>/notify/preview?source_id=<sid>.
  // Returns: { to, subject, body_text, one_time_link_chip, source }
  getNotificationPreview: (id, sourceId) =>
    httpClient.get(`/dsr/${id}/notify/preview`, {
      params: { source_id: sourceId },
    }),

  // Prepare: issue real token + return email body for DPO to review and copy-send.
  prepareNotification: (id, data) =>
    httpClient.post(`/dsr/${id}/prepare-notification`, data),

  // Confirm: record that DPO sent the email manually (audit + status transition).
  confirmOwnerSent: (id, notifId, data) =>
    httpClient.post(`/dsr/${id}/owner-notifications/${notifId}/confirm-sent`, data),

  // Direct send (system sends email via Flask-Mail — kept for future auto-send path).
  sendOwnerEmail: (id, notifId, data) =>
    httpClient.post(`/dsr/${id}/owner-notifications/${notifId}/send-email`, data),

  // DPO adjudication — resolve a source WITHOUT contacting its owner.
  // acknowledgeUncovered: the scan FAILED here and the DPO accepts the gap.
  // Deliberately NOT mark-non-responsive, which would blame an owner who was
  // never written to for a platform-side connection failure.
  acknowledgeUncovered: (id, notifId, data) =>
    httpClient.post(`/dsr/${id}/owners/${notifId}/acknowledge-uncovered`, data),

  // reviewDecision: keep|discard for MEDIUM/LOW-confidence possible matches.
  // 'keep' rules the match genuine — the row then still needs an owner response.
  reviewDecision: (id, notifId, data) =>
    httpClient.post(`/dsr/${id}/owners/${notifId}/review-decision`, data),

  // M2 — the DPO vouches for an answer a machine gave, or refuses it and
  // re-opens the task. Both are HUMAN verbs on the session-authenticated
  // blueprint; the machine channel has no way to reach them.
  confirmApiAnswer: (id, notifId, data) =>
    httpClient.post(`/dsr/${id}/owners/${notifId}/confirm-api-answer`, data),

  rejectApiAnswer: (id, notifId, data) =>
    httpClient.post(`/dsr/${id}/owners/${notifId}/reject-api-answer`, data),

  // skipNotification: the scan worked and the owner is reachable, but the DPO
  // has decided they need not be asked (source being decommissioned, request
  // already handled out-of-band, duplicate of a source already covered).
  // Distinct from both verbs above: nothing here claims a scan failed or that
  // an owner was contacted and stayed silent.
  skipNotification: (id, notifId, data) =>
    httpClient.post(`/dsr/${id}/owners/${notifId}/skip-notification`, data),

  getOwnerResponses: (id) =>
    httpClient.get(`/dsr/${id}/owner-responses`),

  // Spec §3 — aggregate counts (sourcesSearched/hits/noHit/anomalies/allResponded)
  // used by the Processing-tab stats row + Mark-complete enable check.
  getOwnerResponsesSummary: (id) =>
    httpClient.get(`/dsr/${id}/owner-responses/summary`),

  downloadEvidence: (dsrId, notifId) =>
    httpClient.get(`/dsr/${dsrId}/owner-notifications/${notifId}/evidence`, {
      responseType: 'blob',
    }),

  closeRequest: (id, data) =>
    httpClient.post(`/dsr/${id}/close`, data),

  downloadAuditReport: (id, locale = 'en') =>
    httpClient.get(`/dsr/${id}/audit-report`, {
      params: { locale },
      responseType: 'blob',
    }),

  downloadDataPackage: (id) =>
    httpClient.get(`/dsr/${id}/data-package`, { responseType: 'blob' }),
};
